import { useState } from "react";
import { money, safeHarbor, RULES_2026, type EstimatedPayment } from "@ftr/tax-core";
import { Card, Choice, JumpToResult, MoneyField, Pending, Section, StatusField, WhyPanel } from "../components/ui.tsx";
import { Figure, Line, ResultCard } from "../components/figure.tsx";
import { parseMoney } from "../lib/fields.ts";
import { shortDate, todayIso, usd } from "../lib/format.ts";
import type { Profile } from "../lib/profile.ts";

const DUE = RULES_2026.estimatedTax.dueDates;
const FILE_BY = RULES_2026.estimatedTax.fileByToSkipFinalPayment;

/** "Apr 15" from an ISO date. */
function monthDay(iso: string): string {
  return shortDate(iso).replace(/, \d{4}$/, "");
}
function dayAfter(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
/** Payment windows: each payment counts toward the first due date on or after the day it was paid. */
function windowLabel(i: number): string {
  return i === 0 ? `Paid on or before ${monthDay(DUE[0])}` : `Paid ${monthDay(dayAfter(DUE[i - 1]!))} – ${monthDay(DUE[i]!)}`;
}

export function Quarterly({ profile, update }: { profile: Profile; update: (p: Partial<Profile>) => void }) {
  const [priorTax, setPriorTax] = useState("");
  const [priorAgi, setPriorAgi] = useState("");
  const [fullYear, setFullYear] = useState<"yes" | "no" | "">("");
  const [expected, setExpected] = useState("");
  const [paid, setPaid] = useState<string[]>(DUE.map(() => ""));
  const asOf = todayIso();
  // Only windows that have started can hold a payment.
  const windows = DUE.map((d, i) => i).filter((i) => i === 0 || DUE[i - 1]! < asOf);

  const p = {
    priorTax: parseMoney(priorTax),
    priorAgi: parseMoney(priorAgi),
    expected: parseMoney(expected),
    withholding: parseMoney(profile.withholding),
  };
  const paidParsed = windows.map((i) => ({ i, p: parseMoney(paid[i]!) }));
  const bad = (v: ReturnType<typeof parseMoney>) => v.kind === "error" || (v.kind === "value" && v.value.lessThan(0));
  const errors = [
    ...Object.entries(p)
      .filter(([, v]) => bad(v))
      .map(([k]) => LABELS[k as keyof typeof LABELS]),
    ...paidParsed.filter((x) => bad(x.p)).map((x) => windowLabel(x.i).toLowerCase()),
  ];
  const payments: EstimatedPayment[] = paidParsed.flatMap((x) => (x.p.kind === "value" && x.p.value.greaterThan(0) ? [{ amount: x.p.value, paidBy: DUE[x.i]! }] : []));
  const val = (x: (typeof p)[keyof typeof p]) => (x.kind === "value" ? x.value : null);
  const missing: string[] = [];
  if (fullYear === "") missing.push("whether you filed a full-year 2025 return");
  const ready = errors.length === 0 && missing.length === 0;
  const r = ready
    ? safeHarbor(
        {
          status: profile.status,
          priorYearTax: val(p.priorTax),
          priorYearAgi: val(p.priorAgi),
          priorYearFullYearReturn: fullYear === "yes",
          expectedCurrentYearTax: val(p.expected),
          expectedWithholding: val(p.withholding) ?? money("0"),
          payments,
        },
        asOf,
      )
    : null;
  const needs = r?.value.state === "needs_input" ? r.value.required : [];

  return (
    <div className="calc">
      <Card className="inputs">
        <Section label="Last year · 2025 return">
          <StatusField value={profile.status} onChange={(status) => update({ status })} />
          <MoneyField label="2025 total tax" value={priorTax} onChange={setPriorTax} hint="Form 1040, line 24. If you claimed refundable credits such as the earned income credit, the 1040-ES instructions subtract them." />
          <MoneyField label="2025 adjusted gross income" value={priorAgi} onChange={setPriorAgi} hint="Form 1040, line 11" />
          <Choice
            legend="Was 2025 a full 12-month year with a return filed?"
            value={fullYear}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            onChange={setFullYear}
          />
        </Section>
        <Section label="This year · 2026">
          <MoneyField label="Expected 2026 total tax" optional value={expected} onChange={setExpected} hint="If you know it. Our other calculators can estimate it." />
          <MoneyField label="Expected 2026 withholding" optional value={profile.withholding} onChange={(withholding) => update({ withholding })} hint="Federal tax withheld from paychecks" />
        </Section>
        <Section label="Estimated payments made for 2026">
          {windows.map((i) => (
            <MoneyField
              key={i}
              label={windowLabel(i)}
              optional
              value={paid[i]!}
              onChange={(v) => setPaid((cur) => cur.map((x, j) => (j === i ? v : x)))}
              hint={i === 0 ? "When you paid matters: a late payment doesn't make an earlier due date on time." : undefined}
            />
          ))}
        </Section>
      </Card>
      {r?.value.state === "computed" && <JumpToResult text={headline(r.value)} />}

      <div className="results">
        {!r && <Pending missing={missing} errors={errors} />}
        {r?.value.state === "invalid" && <Pending missing={[]} errors={r.value.errors} />}
        {needs.length > 0 && (
          <Pending
            missing={needs.map((n) =>
              n === "priorYearAgi" ? "your 2025 adjusted gross income" : "your 2025 total tax (or your expected 2026 tax)",
            )}
            errors={[]}
          />
        )}
        {r?.value.state === "computed" && (() => {
          const v = r.value;
          const next = v.upcoming.find((u) => u.amount.greaterThan(0));
          const last = DUE[DUE.length - 1]!;
          const showFileBy = !v.noPaymentsNeeded && asOf > DUE[DUE.length - 2]! && asOf <= FILE_BY;
          return (
            <ResultCard title="Quarterly estimate">
              {v.noPaymentsNeeded ? (
                <>
                  <p className="result-label">No more payments needed</p>
                  <p className="big">$0</p>
                  <p className="result-sub">
                    {v.shortDueDates.length
                      ? "You've reached the safe-harbor amount, but you were behind on earlier due dates (below)."
                      : "Your withholding and payments reach the safe-harbor amount, based on what you entered."}
                  </p>
                </>
              ) : v.pastDue.greaterThan(0) ? (
                <>
                  <p className="result-label">Pay now</p>
                  <p className="big">
                    <Figure value={v.pastDue} />
                  </p>
                  <p className="result-sub">
                    {v.afterFinalDueDate
                      ? `The last 2026 due date (${shortDate(last)}) has passed. This is what's left of the safe-harbor amount (${v.basis}).`
                      : "was due on dates that have already passed. Paying today stops the penalty on it from growing."}
                  </p>
                  {next && (
                    <p className="callout">
                      Then pay <strong>{usd(next.amount)}</strong> by {shortDate(next.dueDate)}
                      {v.upcoming.filter((u) => u.amount.greaterThan(0)).length > 1 ? ", and the amounts below after that" : ""}.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="result-label">{v.upcoming.length === 1 ? "Pay by " + shortDate(v.upcoming[0]!.dueDate) : "Pay at each remaining due date"}</p>
                  <p className="big">
                    <Figure value={v.upcoming[0]!.amount} />
                  </p>
                  <p className="result-sub">to reach the safe-harbor amount ({v.basis}).</p>
                </>
              )}
              <div className="lines">
                <Line label="Safe-harbor amount" note={v.basis} value={usd(v.requiredAnnualPayment)} />
                <Line label="Estimated payments made" value={usd(v.paidSoFar)} />
                <Line label="Still to pay" note="after withholding and payments made" value={usd(v.remainingAfterWithholdingAndPayments)} strong />
              </div>
              {!v.noPaymentsNeeded && v.upcoming.length > 0 && (
                <ul className="dates">
                  {v.upcoming.map((u) => (
                    <li key={u.dueDate}>
                      <span>{shortDate(u.dueDate)}</span>
                      <strong>{usd(u.amount)}</strong>
                    </li>
                  ))}
                </ul>
              )}
              {v.shortDueDates.length > 0 && (
                <p className="callout callout--warn">
                  You were behind on {v.shortDueDates.map(monthDay).join(", ")}. An underpayment penalty may apply for the time you were behind. If most of
                  your income came late in the year, the annualized income method (IRS Form 2210, Schedule AI) can lower or remove it.
                </p>
              )}
              {showFileBy && (
                <p className="callout">
                  You can skip the {monthDay(last)} payment if you file your 2026 return by {shortDate(FILE_BY)} and pay everything you owe with it.
                </p>
              )}
              <p className="callout">Pay at IRS Direct Pay or your IRS online account and choose "Estimated tax" for 2026. Save the confirmation number.</p>
              <p className="fine">Due dates and amounts as of {shortDate(asOf)}.</p>
              <WhyPanel trace={r.trace} />
            </ResultCard>
          );
        })()}
      </div>
    </div>
  );
}

const LABELS = {
  priorTax: "2025 total tax",
  priorAgi: "2025 adjusted gross income",
  expected: "expected 2026 tax",
  withholding: "expected 2026 withholding",
};

type Computed = Extract<ReturnType<typeof safeHarbor>["value"], { state: "computed" }>;
function headline(v: Computed): string {
  if (v.noPaymentsNeeded) return "No more payments needed";
  if (v.pastDue.greaterThan(0)) return `Pay ${usd(v.pastDue)} now`;
  return `Pay ${usd(v.upcoming[0]!.amount)} by ${shortDate(v.upcoming[0]!.dueDate)}`;
}
